import type { WidgetResult } from '../../core/hooks/useAnalytics';
import { Site } from '../../core/modules/sites/Site';
import { WidgetType } from '../../core/types/WidgetType';
import { postJSON } from '../../sharedModules/api/apiUtils';
import type { LegacyEventData } from '../../types/LegacyEventData';
import { getHawk, updateHawk } from '../../utils/global';
import { BaseEvent } from '../events/BaseEvent';

import type { EventProduct } from './dealDataToProducts';

export type BaseEventData = {
  fieWidgetsVersion: string;
  context: {
    page: {
      title?: string;
      url?: string;
      type?: string;
      category?: string;
      region?: string;
      tags: string[];
      countryCode?: string;
      sponsored?: boolean;
    };
    content: {
      article: {
        verticals: string[];
        subVerticals: string[];
        language?: string;
        authors: string[];
        id?: string;
        publishDate?: string;
        updateDate?: string;
        cmsCategory?: string;
        cmsChildCategory?: string;
        cmsGrandchildCategory?: string;
      };
    };
    site?: Site;
    experiment: {
      id?: string;
      name?: string;
      variantId?: number;
      variantName?: string;
      experimentSource?: string;
      battleId?: string;
    };
    user: {
      loggedIn: boolean;
      screenWidth: number;
      userAgent: string;
    };
  };
};

export enum Processor {
  HAWKLINKS = 'hawklinks',
  SKIMLINKS = 'skimlinks',
  NONE = 'none',
}

export type EventWidget = {
  type: WidgetType;
  result: WidgetResult | Processor;
  products: EventProduct[];
};

type WidgetsLoadedData = BaseEventData & {
  loadedAt: string;
  widgets: EventWidget[];
};

export type WidgetVisibleData = BaseEventData & {
  visibleAt: string;
  widget: EventWidget;
};

export enum EventArea {
  TERMS_AND_CONDITIONS = 'terms and conditions',
  DETAILS_SECTION_TOGGLE = 'details section toggle',
  SEE_ALL_LINK = 'see all link',
  RETAILER_IMAGE = 'retailer image',
  CTA = 'cta',
  GO_TO_MERCHANT_PAGE_LINK = 'go to merchant page link',
  REVEALED_CODE = 'revealed code',
  COPY_BUTTON = 'copy button',
  SEARCH_BUTTON = 'search button',
  CHIP = 'chip',
  ROUNDEL = 'roundel',
  SIGN_UP_BUTTON = 'sign up button',
  PRODUCT_IMAGE = 'product image',
  MODEL_NAME = 'model name',
  PRICE = 'price',
  AMAZON_PRIME_IMAGE = 'amazon prime image',
  READ_MORE = 'read more',
  SHOW_MORE = 'show more',
  SHOW_LESS = 'show less',
  CLOSE_ICON = 'close icon',
  BACKDROP = 'backdrop',
  ESCAPE_KEY = 'escape key',
  COLLECTION_CARD = 'collection card',
  VIEW_DETAILS_LINK = 'view details link',
  COUPONS_BUTTON = 'coupons button',
  COUPON = 'coupon',
  FAVOURITE_ICON = 'favourite icon',
  UNSPECIFIED = 'unspecified',
}

export enum EventAction {
  GO_TO_RETAILER_PAGE = 'go to retailer page',
  GO_TO_EXTERNAL_PAGE = 'go to external page',
  GO_TO_INTERNAL_PAGE = 'go to internal page',
  MOVE_ON_PAGE = 'move on page',
  OPENED = 'opened',
  CLOSED = 'closed',
  CLOSE_POPUP = 'close popup',
  VIEW_DETAILS = 'view details',
  CLOSE_PRODUCT_INFO_POPUP = 'close product info popup',
  OPEN_COUPONS_POPUP = 'open coupons popup',
  CLOSE_COUPONS_POPUP = 'close coupons popup',
  SEE_ALL_DISCOUNT_CODES = 'see all discount codes',
  REVEAL_CODE = 'reveal code',
  GO_TO_MERCHANT_PAGE = 'go to merchant page',
  COPY_CODE = 'copy code',
  SIGN_UP = 'sign up',
  // TODO(Petr): HAWK-7836 temporary action
  AFFILIATE_LINK_CLICKED = 'affiliate link clicked',
  SEARCH_PRODUCTS = 'search products',
  FAVOURITE = 'favourite',
  UNFAVOURITE = 'unfavourite',
}

export type WidgetInteractedData = BaseEventData & {
  interactedAt: string;
  area: EventArea | string; // Has to be also a string due to hawklinks reading data-hawkify-clicked-area
  action: EventAction;
  widget: EventWidget;
};

export type ItemViewedData = BaseEventData & {
  viewedAt: string;
  area: EventArea;
  widget: EventWidget;
};

export enum FullyQualifiedMessageName {
  WIDGETS_LOADED = 'future.ecomtech.hawk.widgets.v1.WidgetsLoadedEvent',
  WIDGET_VISIBLE = 'future.ecomtech.hawk.widgets.v1.WidgetVisibleEvent',
  WIDGET_INTERACTED = 'future.ecomtech.hawk.widgets.v1.WidgetInteractedEvent',
  ITEM_VIEWED = 'future.ecomtech.hawk.widgets.v1.ItemViewedEvent',
}

export enum EventName {
  HAWK_EVENT = 'hawkEvent',
  WIDGETS_LOADED = 'widgetsLoaded',
  WIDGET_VISIBLE = 'widgetVisible',
  WIDGET_INTERACTED = 'widgetInteracted',
  ITEM_VIEWED = 'itemViewed',
}

export type HawkEvent =
  | {
      name: EventName.HAWK_EVENT;
      data: LegacyEventData;
    }
  | {
      name: EventName.WIDGETS_LOADED;
      fullyQualifiedMessageName: FullyQualifiedMessageName.WIDGETS_LOADED;
      data: WidgetsLoadedData;
    }
  | {
      name: EventName.WIDGET_VISIBLE;
      fullyQualifiedMessageName: FullyQualifiedMessageName.WIDGET_VISIBLE;
      data: WidgetVisibleData;
    }
  | {
      name: EventName.WIDGET_INTERACTED;
      fullyQualifiedMessageName: FullyQualifiedMessageName.WIDGET_INTERACTED;
      data: WidgetInteractedData;
    }
  | {
      name: EventName.ITEM_VIEWED;
      fullyQualifiedMessageName: FullyQualifiedMessageName.ITEM_VIEWED;
      data: ItemViewedData;
    };

const pushToFreyr = (event: HawkEvent): void => {
  window.freyr = window.freyr ?? { cmd: [] };
  window.freyr.cmd.push(() => window.freyr?.pushAndUpdate?.(event.name, event.data));
};

type GargantuanEvent = Exclude<HawkEvent, { name: EventName.HAWK_EVENT }>;

const postToGargantuan = async (event: GargantuanEvent, site?: Site): Promise<void> => {
  const isVanilla = site && ['responsive', 'progressive'].includes(site.platform);
  const prodUrl = isVanilla
    ? `/.events-handler/${event.fullyQualifiedMessageName}`
    : `https://eventsproxy.gargantuan.futureplc.com/${event.fullyQualifiedMessageName}`;
  const devUrl = `https://events-proxy-service.main.qa.sse-prod-eks-euw1.futureplc.engineering/${event.fullyQualifiedMessageName}`;
  const url = process.env.GARGANTUAN_PROXY === 'prod' ? prodUrl : devUrl;
  await postJSON(url, event.data);
};

export const sendEvent = (event: BaseEvent): void => {
  const { hawkEvent } = event;

  // Store the data in the global object so we can check it in Cypress tests.
  // This is done here so that it's only used for client-side GA.
  // Setting it globally in Node would cause the memory to increase over time.
  const { events, site } = getHawk();
  updateHawk({
    events: [...(events ?? []), hawkEvent],
  });

  if (hawkEvent.name === EventName.HAWK_EVENT) {
    pushToFreyr(hawkEvent);
  } else {
    postToGargantuan(hawkEvent, site);
  }
};
